import type { AuthUser } from '@type/auth';
import type { ModuleId } from '@type/module';

/**
 * Route constants — canonical source (tvet-piu-frontend pattern).
 * @see config/routes.ts for backward-compatible re-exports.
 */
export const ROUTES = {
  login: '/login',
  gapLogin: '/gap/login',
  dashboard: '/dashboard',
  visual: '/visual',
  coes: '/coes',
  procurement: '/procurement',
  contractMgmt: '/contract-mgmt',
  accounts: '/accounts',
  training: '/training',
  gap: '/gap',
  budget: '/budget',
} as const;

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];

/** URL path for each module — one route per role-accessible section. */
export const MODULE_ROUTES: Record<ModuleId, AppRoute> = {
  dashboard: ROUTES.dashboard,
  visual: ROUTES.visual,
  coes: ROUTES.coes,
  procurement: ROUTES.procurement,
  contractmgmt: ROUTES.contractMgmt,
  accounts: ROUTES.accounts,
  training: ROUTES.training,
  gap: ROUTES.gap,
  budget: ROUTES.budget,
};

export const LOGIN_PATH = ROUTES.login;
export const GAP_LOGIN_PATH = ROUTES.gapLogin;

export function getLoginPathForLocation(pathname: string | null | undefined): string {
  const normalized = (pathname || '').replace(/\/+$/, '') || '/';
  if (normalized === MODULE_ROUTES.gap || normalized.startsWith(`${MODULE_ROUTES.gap}/`)) {
    return GAP_LOGIN_PATH;
  }
  return LOGIN_PATH;
}

export function moduleToPath(moduleId: ModuleId): AppRoute | null {
  return MODULE_ROUTES[moduleId] ?? null;
}

export function pathToModule(pathname: string | null | undefined): ModuleId | null {
  const normalized = (pathname || '').replace(/\/+$/, '') || '/';
  const hit = Object.entries(MODULE_ROUTES).find(([, path]) => path === normalized);
  return hit ? (hit[0] as ModuleId) : null;
}

export function getDefaultPath(user: AuthUser | null | undefined): string {
  if (!user) return LOGIN_PATH;
  return moduleToPath(user.defaultModule) ?? LOGIN_PATH;
}

export function canAccessPath(user: AuthUser | null | undefined, pathname: string): boolean {
  if (!user) return false;
  const moduleId = pathToModule(pathname);
  if (!moduleId) return false;
  return user.modules.includes(moduleId);
}

export function resolvePostLoginPath(
  user: AuthUser,
  fromPath: string | null | undefined,
): string {
  if (fromPath && canAccessPath(user, fromPath)) return fromPath;
  return getDefaultPath(user);
}
